<template>
    <div class="application-form-wrapper p-3 p-xl-4 min-vh-100">
        <b-container>
            <div class="d-flex justify-content-lg-between justify-content-center align-items-center mb-4">
                    <h3 class="mb-0 font-weight-bold d-none d-lg-block">{{ $t('special_student_application_form')}}</h3>
                    <div class="d-flex gap-2">
                        <b-button class="btn-40 d-lg-flex shadow-sm" variant="outline-secondary"
                                  @click="changeLocale('en')" v-if="locale ==='tr'">
                            EN
                        </b-button>
                        <b-button class="btn-40 d-lg-flex shadow-sm" variant="outline-secondary"
                                  @click="changeLocale('tr')" v-else>
                            TR
                        </b-button>
                        <b-button variant="outline-primary">
                            <CountDown
                                :localStorageKey="'specialstudentSessionDeadline'"
                                deadline="2026-12-31 23:59:59"
                                :showDays="false"
                                :show-hours="false"
                                ref="countdown"
                                customClass="text-primary font-weight-bold"
                                @expired="onCountdownExpired"
                            />
                        </b-button>
                        <b-button variant="outline-danger" @click="logout">
                            <i class="ri-logout-circle-line mr-1 top-minus-1 align-middle"></i>
                            {{ $t('logout') }}
                        </b-button>
                    </div>
            </div>
            <div class="d-lg-none mb-2">
                <h3 class="mb-0 font-weight-bold">{{ $t('special_student_application_form')}}</h3>
            </div>
            <b-card no-body class="shadow-sm p-3">
                <SuccessRevision v-if="isApplicationAccepted" type="accepted" />
                <SuccessRevision v-else-if="isRevisionSuccess" :type="isRejectedCourses ? 'courses' : 'documents'" />
                <RejectedDocumentsUpload
                    v-else-if="isRejectedStudentAffairs"
                    :countDown="$refs.countdown?.timeLeft.difference"
                    @success="isRevisionSuccess = true"
                />
                <RejectedDocumentsUpload
                    v-else-if="isWaitingApproval"
                    :isWaitingMode="true"
                    :countDown="$refs.countdown?.timeLeft.difference"
                />
                <RejectedCoursesSelection
                    v-else-if="isRejectedCourses"
                    :countDown="$refs.countdown?.timeLeft.difference"
                    @success="isRevisionSuccess = true"
                />
                <RejectedCoursesSelection
                    v-else-if="isWaitingSecretary"
                    :isWaitingMode="true"
                    :countDown="$refs.countdown?.timeLeft.difference"
                />
                <b-tabs v-else v-model="tabIndex" >
                    <b-tab :title="$t('information')" :active="tabIndex === 0" :disabled="maxTabIndex < 0">
                        <InfoTab :initial-data="specialStudentData" @next="nextTab($event)" :countDown="$refs.countdown?.timeLeft.difference" v-if="tabIndex===0" />
                    </b-tab>
                    <b-tab :title="$t('program_select')" :active="tabIndex === 1" :disabled="maxTabIndex < 1">
                        <ProgramSelectionTab :initial-data="specialStudentData" @next="nextTab($event)" :countDown="$refs.countdown?.timeLeft.difference" v-if="tabIndex===1" />
                    </b-tab>
                    <b-tab :title="$t('course_registration')" :active="tabIndex === 2" :disabled="maxTabIndex < 2">
                        <CourseRegistrationTab @next="nextTab($event)" :countDown="$refs.countdown?.timeLeft.difference" v-if="tabIndex===2" />
                    </b-tab>
                    <b-tab :title="$t('documents')" :active="tabIndex === 3" :disabled="maxTabIndex < 3">
                        <DocumentsTab :initial-data="specialStudentData" @next="nextTab($event)" :countDown="$refs.countdown?.timeLeft.difference" @prev="prevTab" v-if="tabIndex===3" />
                    </b-tab>
                    <b-tab :title="$t('summary')" :active="tabIndex === 4" :disabled="maxTabIndex < 4">
                        <SummaryTab  @prev="prevTab" @submit="onSubmit"  v-if="tabIndex===4"/>
                    </b-tab>
                </b-tabs>
            </b-card>
            <b-modal ref="warningModal" hide-header hide-footer centered size="md">
                <div class="d-flex flex-column align-items-center justify-content-center p-4 text-center">
                    <div class="mb-3 text-info">
                        <i class="ri-information-line" style="font-size: 3.5rem;"></i>
                    </div>
                    <div class="message mb-4 font-weight-bold text-dark" style="font-size: 1.1rem; line-height: 1.6;" v-html="$t('application_resume_info_text')">

                    </div>
                    <b-button variant="info" class="font-weight-bold shadow-sm px-5 text-white" @click="closeWarningModal">
                        {{ $t('ok') }}
                    </b-button>
                </div>
            </b-modal>

        </b-container>
    </div>
</template>

<script>
import InfoTab from "../components/tabs/InfoTab.vue";
import ProgramSelectionTab from "../components/tabs/ProgramSelectionTab.vue";
import CourseRegistrationTab from "../components/tabs/CourseRegistrationTab.vue";
import DocumentsTab from "../components/tabs/DocumentsTab.vue";
import SummaryTab from "../components/tabs/SummaryTab.vue";
import RejectedDocumentsUpload from "../components/RejectedDocumentsUpload.vue";
import RejectedCoursesSelection from "../components/RejectedCoursesSelection.vue";
import SuccessRevision from "../components/SuccessRevision.vue";
import { localeChanged } from 'vee-validate';
import CountDown from "@/components/elements/CountDown.vue";

export default {
    name: "FormIndex",
    components: {
        CountDown,
        InfoTab,
        ProgramSelectionTab,
        CourseRegistrationTab,
        DocumentsTab,
        SummaryTab,
        RejectedDocumentsUpload,
        RejectedCoursesSelection,
        SuccessRevision
    },
    data() {
        return {
            tabIndex: 0,
            maxTabIndex: 0,
            specialStudentData: null,
            isRejectedStudentAffairs: false,
            isRejectedCourses: false,
            isWaitingApproval: false,
            isWaitingSecretary: false,
            isApplicationAccepted: false,
            isRevisionSuccess: false
        }
    },
    metaInfo() {
        return {
            title: this.$t('special_student_application_form')
        }
    },
    computed: {
        locale() {
            return this.$store.getters['getLocale'] || this.$i18n.locale;
        }
    },
    async mounted() {
        await this.$store.getters['SpecialStudentApplicationRegister/checkIsLogin']
        const data = this.getWithExpiry('specialStudentData',true)
        const warningModal = this.getWithExpiry('sepecialStudentWarningModal',true)
        if (!warningModal){
            this.openWarningModal()
        }

        if (data?.application_status === 'document_waiting') {
            this.isRejectedStudentAffairs = true;
        } else if (data?.application_status==='course_rejected'){
            this.isRejectedCourses = true;
        } else if (data?.application_status === 'waiting_approval') {
            this.isWaitingApproval = true;
        } else if (data?.application_status === 'waiting_secretary_approval') {
            this.isWaitingSecretary = true;
        } else if (data?.application_status === 'accepted') {
            this.isApplicationAccepted = true;
        } else{
            this.switchTabIndex(data?.step_type)
        }
    },
    methods: {
        openWarningModal(){
            this.$refs.warningModal.show();
        },
        closeWarningModal(){
            this.setWithExpiry('sepecialStudentWarningModal',true,this.$refs.countdown?.timeLeft.difference,true)
            this.$refs.warningModal.hide();
        },
        switchTabIndex(status){
            if (!status) return
            if (status==='information'){
                this.nextTab(0)
            }else if (status==='program_selection'){
                this.nextTab(1)
            }else if (status==='course_registration'){
                this.nextTab(2)
            }else if (status==='document_upload'){
                this.nextTab(3)
            }else if (status==='application_preview'){
                this.nextTab(4)
            }
        },
        changeLocale(locale) {
            this.$i18n.locale = locale;
            this.$store.commit('setLocale', locale);
            localeChanged();
        },
        nextTab(tab) {
            this.maxTabIndex = tab;
            this.$nextTick(() => {
                this.tabIndex = tab;
            });

        },
        prevTab() {
            if (this.tabIndex > 0) {
                this.tabIndex--;
            }
        },
        onSubmit() {
            this.$toast.success(this.$t('application_success_title'));
        },
        onCountdownExpired(){
            this.$store.dispatch('SpecialStudentApplicationRegister/logout')
        },
        logout(){
            this.$store.dispatch('SpecialStudentApplicationRegister/logout')
        }
    }
}
</script>

<style>
</style>
